import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowLeft, Send } from "lucide-react";
import Logo, { KiteMark } from "@/components/Logo";
import FolkBorder from "@/components/FolkBorder";
import { Reveal } from "@/components/Reveal";
import Footer from "@/sections/Footer";
import api from "@/lib/api";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

const EventCard = ({ ev, past = false, delay = 0 }) => {
  const parts = (ev.event_date || "").split("-");
  const day = parts[2] ? String(parseInt(parts[2], 10)) : null;
  const month = parts[1] ? MONTHS[parseInt(parts[1], 10) - 1] : null;

  return (
    <Reveal delay={delay}>
      <article
        className={`h-full flex flex-col bg-paper border-2 border-navy/15 rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10 transition-all duration-300 ${
          past ? "opacity-70" : ""
        }`}
        data-testid={past ? "past-event-card" : "upcoming-event-card"}
      >
        <div className="h-[3px] bg-gradient-to-r from-verm via-marigold to-kite" />
        <div className="p-6 flex flex-col gap-4 flex-1">
          <div className="flex items-center gap-4">
            <span
              className={`shrink-0 w-16 h-16 rounded-2xl grid place-items-center border-[3px] ${
                past ? "bg-paperdeep border-navy/25 text-navy/60" : "bg-marigold border-navy text-navy"
              }`}
              data-testid="event-date-badge"
            >
              {day ? (
                <span className="text-center leading-none">
                  <span className="block font-display font-bold text-2xl">{day}</span>
                  <span className="block text-[10px] font-bold tracking-[0.2em] mt-1">{month}</span>
                </span>
              ) : (
                <KiteMark className="w-8 h-8" />
              )}
            </span>
            <div className="min-w-0">
              <h3
                className="font-display font-bold text-navy text-xl leading-tight"
                data-testid="event-card-title"
              >
                {ev.title}
              </h3>
              {ev.location ? (
                <p className="mt-1.5 flex items-center gap-1.5 font-body text-navy/70 text-sm" data-testid="event-card-location">
                  <MapPin className="w-3.5 h-3.5 text-verm shrink-0" />
                  {ev.location}
                </p>
              ) : null}
            </div>
          </div>
          {ev.description ? (
            <p className="font-body text-navy/80 text-sm leading-relaxed" data-testid="event-card-description">
              {ev.description}
            </p>
          ) : null}
        </div>
      </article>
    </Reveal>
  );
};

const EventsPage = () => {
  const [content, setContent] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    Promise.all([api.get("/content"), api.get("/events")])
      .then(([c, e]) => {
        setContent(c.data);
        setEvents(e.data);
      })
      .catch(() => {});
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => (e.event_date || "9999-99-99") >= today);
  const past = events.filter((e) => (e.event_date || "9999-99-99") < today).reverse();

  return (
    <div className="min-h-screen bg-paper font-body text-ink antialiased" data-testid="events-page">
      <div className="grain-overlay" aria-hidden="true" />
      <FolkBorder />

      <header className="sticky top-0 z-40 bg-paper/90 backdrop-blur-md shadow-[0_1px_0_rgba(20,53,92,0.12)]">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 py-3">
          <a href="/" data-testid="events-home-link" aria-label="Back to Wind Skaters home">
            <Logo />
          </a>
          <div className="flex items-center gap-3">
            <a
              href="/"
              data-testid="events-back-button"
              className="inline-flex items-center gap-2 font-semibold text-navy border-2 border-navy/20 px-5 py-2 rounded-full hover:border-verm hover:text-verm transition-colors duration-300"
            >
              <ArrowLeft className="w-4 h-4" />
              Home
            </a>
            <a
              href="/#contact"
              data-testid="events-fly-with-us"
              className="hidden sm:inline-flex items-center gap-2 bg-navy text-paper font-semibold px-6 py-2 rounded-full border-2 border-marigold hover:bg-verm hover:border-verm transition-colors duration-300"
            >
              Fly With Us
            </a>
          </div>
        </div>
      </header>

      <section className="relative py-16 sm:py-24 text-center px-5" data-testid="events-hero">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="font-script text-verm text-4xl sm:text-5xl" data-testid="events-script">
              {content?.events_script || "Where The Wind Takes Us"}
            </p>
            <h1 className="mt-4 font-display font-bold text-navy text-4xl sm:text-6xl" data-testid="events-heading">
              {content?.events_heading || "Events & Meetups"}
            </h1>
            <span className="block mt-6 h-1.5 w-32 mx-auto rounded-full bg-gradient-to-r from-verm via-marigold to-kite" />
            <p className="mt-6 font-body text-navy/80 text-base sm:text-lg leading-relaxed" data-testid="events-sub">
              {content?.events_sub ||
                "Festival flies, beach sessions and community meetups — come fly with us. Bring the family, bring a kite, or just bring yourself."}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 px-5" data-testid="upcoming-events">
        <div className="max-w-7xl mx-auto">
          <Reveal className="flex items-center gap-4 mb-10">
            <h2 className="font-display font-bold text-navy text-2xl sm:text-3xl whitespace-nowrap" data-testid="upcoming-heading">
              Upcoming Flies
            </h2>
            <span className="flex-1 h-px bg-navy/15" />
          </Reveal>

          {upcoming.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="upcoming-events-grid">
              {upcoming.map((ev, i) => (
                <EventCard key={ev.id} ev={ev} delay={(i % 3) * 0.08} />
              ))}
            </div>
          ) : (
            <div
              className="border-2 border-dashed border-navy/25 rounded-2xl p-14 text-center font-body text-navy/60"
              data-testid="events-empty"
            >
              <KiteMark className="w-12 h-12 mx-auto mb-4" />
              No events on the calendar yet — the wind is brewing. Add events from the admin panel.
            </div>
          )}

          {past.length ? (
            <div className="mt-20" data-testid="past-events">
              <Reveal className="flex items-center gap-4 mb-10">
                <h2 className="font-display font-bold text-navy/70 text-2xl sm:text-3xl whitespace-nowrap" data-testid="past-heading">
                  Flies We Remember
                </h2>
                <span className="flex-1 h-px bg-navy/15" />
              </Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {past.slice(0, 6).map((ev, i) => (
                  <EventCard key={ev.id} ev={ev} past delay={(i % 3) * 0.08} />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>

      <section className="bg-navy py-16 px-5" data-testid="events-cta-band">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-script text-marigold text-3xl sm:text-4xl" data-testid="events-cta-script">
            {content?.events_cta_script || "Want Wind Skaters at your festival?"}
          </p>
          <p className="mt-3 font-body text-paper/80" data-testid="events-cta-text">
            {content?.events_cta_text || "We fly at community gatherings, school days and kite festivals across Odisha and beyond."}
          </p>
          <a
            href="/#contact"
            data-testid="events-cta-button"
            className="mt-7 inline-flex items-center gap-2 bg-marigold text-navy font-bold px-8 py-3.5 rounded-full border-2 border-paper/40 hover:bg-paper transition-colors duration-300"
          >
            Invite Us To Fly
            <Send className="w-4 h-4" />
          </a>
        </div>
      </section>

      {content ? <Footer content={content} /> : null}
    </div>
  );
};

export default EventsPage;
